import React from 'react'
import { Divider, Row, Col, Form, Input, Select, Button, Skeleton, message, Typography } from 'antd'
import * as deepObject from 'deep-object-diff'
import { FormComponent } from '../../../BaseComponent'
import { PartnerKeyTypeArray } from '../../../../interfaces/IIntegration'
import IShelter from '../../../../interfaces/IShelter'
import { IntegrationConstants } from '../../../../helpers/enums/Integration'
import { SearchRequest } from '../../../../services/api/interfaces/ApiRequest'
import { PartnerAuthRequest } from '../../../../services/integration/interfaces/ApiRequest'

type IntegrationAuthProps = {
  authUuid?: string | null,
  error: any | null

  loading: boolean,
  contentLoading?: boolean
  history: any,

  shelters: IShelter[] | null

  loadShelters: (args: SearchRequest) => Promise<any>,

  authPartner: (args: PartnerAuthRequest) => Promise<any>
}

export default class IntegrationAuth extends FormComponent<IntegrationAuthProps> {

  static defaultProps = {
    contentLoading: true
  }

  state = {
    saving: false,
    loaded: false,

    partnerKey: '',
    shelterUuid: ''
  }

  componentDidMount() {
    const { shelters, loadShelters } = this.props

    if (!shelters) {
      loadShelters({
        search: {
          deleted: false,
          organization_branches: {
            with: {
              deleted: false
            }
          }
        },
        options: {
          limit: 9999
        }
      })
    }
  }

  componentDidUpdate(prevProps: IntegrationAuthProps, prevState: any) {
    const wasLoading = prevProps.loading && !this.props.loading
    let newState = Object.assign({}, this.state)

    if (!this.props.contentLoading && !newState.loaded) {
      newState.loaded = true
    }

    if (this.state.loaded) {
      if (this.state.saving && wasLoading) {
        newState.saving = false

        if (this.props.error) {
          message.error(`Could not make authentication with partner. Reason ${this.props.error}`)
        }
      }
    }

    const hasChanged = Object.keys(deepObject.diff(this.state, newState)).length > 0
    if (hasChanged) {
      this.setState(newState)
    }
  }

  private handleSave = (values: any) => {
    console.log(values)
    const { authPartner } = this.props
    const { partnerKey } = this.state

    const credentials = IntegrationConstants.CREDENTIALS_FIELDS.find(p => p.partner_key === partnerKey)

    const payload: PartnerAuthRequest = {
      endpoint: credentials?.endpoint,
      ...values
    }

    this.setState({
      saving: true
    })
    authPartner(payload)
  }

  renderPartners = () => {
    return (<div style={{ marginTop: 4 }}>
      <Divider />
      <Typography.Title level={5}>Shelter Management Software</Typography.Title>
      <Select
        placeholder="Partner"
        allowClear={true}
        options={PartnerKeyTypeArray.map(partnerKey => {
          return {
            label: partnerKey,
            value: partnerKey
          }
        })}
        style={{
          width: 200
        }}
        onChange={(partnerKey: string) => this.setState({ partnerKey })}
      />
    </div>
    )
  }

  renderShelters = () => {
    const { shelters } = this.props
    const { partnerKey } = this.state
    if (!partnerKey) return null

    return (
      <div style={{ marginTop: 4 }}>
        <Divider />
        <Typography.Title level={5}>Shelter</Typography.Title>
        <Select
          placeholder="Shelter"
          allowClear={true}
          showSearch={true}
          options={shelters?.map(shelter => {
            return {
              label: shelter.name,
              value: shelter.uuid
            }
          })}
          style={{
            width: 200
          }}
          filterOption={(input, option) => {
            if (!option?.label || typeof option.label !== 'string') return false

            return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }}
          onChange={(shelterUuid: string) => this.setState({ shelterUuid })}
        />
      </div>
    )
  }

  renderBranches = () => {
    const { shelters } = this.props
    const { shelterUuid } = this.state
    if (!shelterUuid) return null

    const branches = shelters?.find(p => p.uuid === shelterUuid)?.organization_branches || []

    return (
      <>
        <Divider />
        <Typography.Title level={5}>Shelter Location</Typography.Title>
        <Form.Item
          name="branch_uuid"
          rules={[
            {
              required: true,
              message: 'Required field'
            }
          ]}
        >
          <Select
            placeholder="Location"
            allowClear={true}
            options={branches?.map(branch => {
              return {
                label: branch.name,
                value: branch.uuid
              }
            })}
            style={{
              width: 200
            }}
          />
        </Form.Item>
      </>

    )
  }

  renderTypeKey = () => {
    const { shelterUuid } = this.state
    if (!shelterUuid) return null

    return (
      <>
        <Divider />
        <Typography.Title level={5}>Integration Type</Typography.Title>
        <Form.Item
          name="type_key"
          initialValue="ADOPTION"
          rules={[
            {
              required: true,
              message: 'Required field'
            }
          ]}
        >
          <Select
            placeholder="Type Key"
            options={[
              { label: 'ADOPTION', value: 'ADOPTION' },
              { label: 'LOST', value: 'LOST' },
              { label: 'FOUND', value: 'FOUND' }
            ]}
            style={{
              width: 200
            }}
          />
        </Form.Item>
      </>
    )
  }

  mountFields = () => {
    const { partnerKey } = this.state
    if (!partnerKey || partnerKey.length === 0) return null

    const credentials = IntegrationConstants.CREDENTIALS_FIELDS.find(p => p.partner_key === partnerKey)
    if (!credentials) return null

    return (
      <Row gutter={24}>
        {
          Object.keys(credentials.fields).map(key => {
            const field = credentials.fields[key]

            return (
              <Col key={`credential-field-${key}`}>
                <Form.Item
                  label={field.label}
                  name={['credentials', key]}
                  rules={[
                    {
                      required: field.required,
                      message: 'Required field'
                    }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            )
          })
        }
      </Row>
    )
  }

  render() {
    const { loaded, saving, shelterUuid } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    return (
      <Form
        name="integration_auth"
        layout="vertical"
        onFinish={this.handleSave}
        style={{
          marginTop: 30
        }}
      >
        <Col>
          <Col>
            <Typography.Title level={5}>Integration description</Typography.Title>
            <Typography.Text>This is the short description that will identify your integration. As an Adopets standard, you can use a combination of shelter name + shelter location. </Typography.Text>
            <Form.Item
              name="description"
              rules={[
                {
                  required: true,
                  message: 'Description is required'
                }
              ]}
            >
              <Input placeholder="Integration Description" />
            </Form.Item>
          </Col>

          <Col>
            {this.renderPartners()}
          </Col>

          <Col style={{ marginTop: 4 }}>
            {this.renderShelters()}
          </Col>

          <Col>
            {this.renderBranches()}
          </Col>

          <Col>
            {this.renderTypeKey()}
          </Col>
        </Col>

        <Divider />

        {
          this.mountFields()
        }

        <Form.Item style={{ marginTop: 32 }}>
          <Button type="primary" htmlType="submit" size="middle" loading={saving} disabled={!shelterUuid}>
            Continue
          </Button>
        </Form.Item>
      </Form>
    )
  }
}
